/**
 * EmptyState Component
 * Professional empty state pattern for PE firm
 * Shows when no data is available with helpful guidance
 */

import React from 'react';
import { LucideIcon } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  /** Optional artwork shown instead of the icon; light/dark variants follow the theme. */
  illustration?: {
    light: string;
    dark: string;
  };
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  illustration,
  className,
}) => {
  return (
    <div className={cn('flex flex-col items-center justify-center py-12 px-4 text-center', className)}>
      {/* Illustration or icon */}
      {illustration ? (
        <>
          <img
            src={illustration.light}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="mb-5 h-36 w-36 rounded-2xl object-cover dark:hidden"
          />
          <img
            src={illustration.dark}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="mb-5 hidden h-36 w-36 rounded-2xl object-cover dark:block"
          />
        </>
      ) : (
        <div className="mb-4 rounded-full bg-muted p-6">
          <Icon className="h-12 w-12 text-muted-foreground" />
        </div>
      )}

      {/* Title */}
      <h3 className="text-xl font-semibold text-foreground mb-2">
        {title}
      </h3>

      {/* Description */}
      <p className="text-muted-foreground max-w-md mb-6">
        {description}
      </p>

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className="flex flex-col sm:flex-row gap-3">
          {action && (
            <Button onClick={action.onClick} size="lg">
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button onClick={secondaryAction.onClick} variant="outline" size="lg">
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};


